/**
 * MoEngage web ↔ mobile parity (docs/moengage_events_handover.md).
 *
 * The app sends journey events under four common names; the identifying
 * attribute (page_name / stage_name / cta_name) says which page, stage or CTA
 * fired it. Keys of EKYC_JOURNEY_MAP are the legacy per-page names: call sites
 * keep using EVENTS.* (legacy), and trackEvent() sends the common name to
 * MoEngage instead. CleverTap / GTM / NotifyVisitors are separate calls and
 * keep receiving the legacy names.
 *
 * - `fixed`   added automatically; wins over caller props (so page_name on
 *             loaded/proceeded is always the consolidated value).
 * - `dynamic` filled by the caller or auto-filled by track.js; debug builds
 *             warn when one is missing.
 *
 * Copy names, keys and values verbatim from the handover — including case,
 * `MSG-whatsapp`, `Client_Code` and the `_en` suffix on cta_name values.
 */

const PAGE_LOADED = 'ekyc_page_loaded';
const PAGE_PROCEEDED = 'ekyc_page_proceeded';
const STAGE_VERIFIED = 'ekyc_stage_verified';
const CTA_INITIATED = 'ekyc_page_cta_initiated';

const ME = ['mobile_number', 'email_id'];

const entry = (event, fixed, dynamic) => Object.freeze({
    event,
    fixed: Object.freeze({ ...fixed }),
    dynamic: Object.freeze([...dynamic]),
});

export const EKYC_JOURNEY_MAP = Object.freeze({
    registration_page_loaded_en: entry(PAGE_LOADED, { page_name: 'registration_page' }, ['mobile_number']),
    panpage_loaded_en: entry(PAGE_LOADED, { page_name: 'panpage' }, ME),
    panverifiedpage_loaded_en: entry(PAGE_LOADED, { page_name: 'panverifiedpage' }, ME),
    selfie_screen_loaded_en: entry(PAGE_LOADED, { page_name: 'selfie_screen' }, ME),
    signature_loaded__en: entry(PAGE_LOADED, { page_name: 'signature' }, ME),
    odetails_loaded__en: entry(PAGE_LOADED, { page_name: 'odetails' }, ME),

    registration_page_proceed_en: entry(PAGE_PROCEEDED, { page_name: 'registration_page' }, ['mobile_number', 'full_name']),
    panpage_proceed_en: entry(PAGE_PROCEEDED, { page_name: 'panpage' }, ME),
    panverifiedpage_proceed_en: entry(PAGE_PROCEEDED, { page_name: 'panverifiedpage' }, ME),
    selfie_screen_proceed_en: entry(PAGE_PROCEEDED, { page_name: 'selfie_screen' }, ME),
    signature_proceed__en: entry(PAGE_PROCEEDED, { page_name: 'signature_proceed' }, ME),

    otp_verified_success_en: entry(STAGE_VERIFIED,
        { stage_name: 'otp_verified', truecaller_verified: 'no', 'MSG-whatsapp': true }, ['mobile_number', 'ekyc_user_id']),
    email_google_signin_success_en: entry(STAGE_VERIFIED,
        { stage_name: 'email_google_signin', page_name: 'OTPEMAIL', signin_with_google: 'yes' }, ME),
    // + signin_with_google: 'yes' passed by the caller on the Google path
    email_otp_verified_success_en: entry(STAGE_VERIFIED, { stage_name: 'email_otp_verified', page_name: 'OTPEMAIL' }, ME),
    digilocker_success_en: entry(STAGE_VERIFIED, { stage_name: 'digilocker', page_name: 'PAN Page', aadhaar_linked: 'yes' }, ME),
    bank_pennydrop_success_en: entry(STAGE_VERIFIED, { stage_name: 'bank_pennydrop', page_name: 'Bank Page' }, ME),
    bank_rpd_success_en: entry(STAGE_VERIFIED, { stage_name: 'bank_rpd', page_name: 'Bank Page' }, ME),
    income_proof_success_en: entry(STAGE_VERIFIED, { stage_name: 'income_proof', page_name: 'Other Details Page' }, ME),
    aof_generated_success_en: entry(STAGE_VERIFIED, { stage_name: 'aof_generated', page_name: 'Other Details Page' }, ME),
    esign_completed_success_en: entry(STAGE_VERIFIED, { stage_name: 'esign_completed', page_name: 'Other Details Page' }, ME),
    account_creation_success_en: entry(STAGE_VERIFIED,
        { stage_name: 'account_creation', page_name: 'Other Details Page' }, ['mobile_number', 'Client_Code']),

    email_google_signin_initiated_en: entry(CTA_INITIATED,
        { cta_name: 'email_google_signin_initiated_en', page_name: 'OTPEMAIL', signin_with_google: 'yes' }, ME),
    email_manually_submitted_en: entry(CTA_INITIATED, { cta_name: 'email_manually_submitted_en', page_name: 'OTPEMAIL' }, ME),
    digilocker_initiated_en: entry(CTA_INITIATED, { cta_name: 'digilocker_initiated_en', page_name: 'PAN Page' }, ME),
    bank_pennydrop_initiated_en: entry(CTA_INITIATED, { cta_name: 'bank_pennydrop_initiated_en', page_name: 'Bank Page' }, ME),
    bank_rpd_initiated_en: entry(CTA_INITIATED, { cta_name: 'bank_rpd_initiated_en', page_name: 'Bank Page' }, ME),
    income_proof_initiated_en: entry(CTA_INITIATED, { cta_name: 'income_proof_initiated_en', page_name: 'Other Details Page' }, ME),
    esign_completed_failed_en: entry(CTA_INITIATED, { cta_name: 'esign_completed_failed_en', page_name: 'Other Details Page' }, ME),
    account_under_verification_en: entry(CTA_INITIATED,
        { cta_name: 'account_under_verification_en', page_name: 'Account Under Verification Page' }, ME),
});

/** Main events: names unchanged, fixed values + dynamic keys per the handover. */
export const EKYC_MAIN_EVENTS = Object.freeze({
    lead_verified_sa: entry('lead_verified_sa',
        { truecaller_verified: 'no', 'MSG-whatsapp': true }, ['mobile_number', 'ekyc_user_id']),
    Pan_Verified_CDP: entry('Pan_Verified_CDP', {}, [...ME, 'ekyc_user_id']),
    Bank_Verified_CDP: entry('Bank_Verified_CDP', {}, [...ME, 'ekyc_user_id']),
    document_upload_completed_sa: entry('document_upload_completed_sa', { page_name: 'Signature Page' }, ME),
    fno_selected: entry('fno_selected', { page_name: 'Other Details Page' }, ME),
    esign_done_sa: entry('esign_done_sa', { page_name: 'Other Details Page' }, ME),
    // The app sends mobile_number / email_id as '' on these two; web sends the real values when known.
    add_funds: entry('add_funds', { page_name: 'Add fund Page' }, ME),
    funds_added: entry('funds_added', { page_name: 'Add fund Page' }, ME),
});

const REJECTION_PAGE = Object.freeze({ page_name: 'rejection_review' });

/**
 * Other events (handover §3) that have a matching web flow. Names unchanged.
 * Not sent from web: mo_investor_android_open_account_final_submit_sa (needs
 * confirmation), kra_photo_recheck_routed_en, SA_Demat_KYC_Page_Open_US_Equity_account,
 * SA_US_Creat_new_account_next, ekyc_dropout — no such flow on web
 * (docs/moengage-parity-gap.md §3).
 */
export const EKYC_OTHER_EVENTS = Object.freeze({
    lead_filled_cdp: entry('lead_filled_cdp', {}, ['mobile', 'name', 'ekyc_user_id']),
    lead_verified_cdp: entry('lead_verified_cdp',
        { truecaller_verified: 'no', 'MSG-whatsapp': true }, ['mobile_number', 'ekyc_user_id']),
    pan_verification_completed: entry('pan_verification_completed', { page_name: 'PAN Page' }, ME),
    bank_verification_completed: entry('bank_verification_completed', { page_name: 'Bank Page' }, ME),
    odetails_proceed__en: entry('odetails_proceed__en', { page_name: 'Other Details Page' }, ['mobile_number', 'FNO opted']),
    location_permission_en: entry('location_permission_en', { page_name: 'Photo Page' }, ['mobile_number', 'location_permission_status']),
    location_denied_en: entry('location_denied_en', { page_name: 'Photo Page' }, ['mobile_number', 'location_permission_status']),
    nominee_added: entry('nominee_added', { page_name: 'Nominee Page' }, ME),
    income_account_aggregator: entry('income_account_aggregator', { page_name: 'INCOME' }, [...ME, 'account_aggregator']),
    income_proof_viewed_en: entry('income_proof_viewed_en', { page_name: 'Other Details Page' }, ME),
    income_proof_not_visited_en: entry('income_proof_not_visited_en', { page_name: 'Other Details Page' }, ME),
    rejection_page_loaded_en: entry('rejection_page_loaded_en', REJECTION_PAGE, ['mobile_number', 'total_rejected_items']),
    rejection_page_viewed_en: entry('rejection_page_viewed_en', REJECTION_PAGE,
        ['mobile_number', 'session_type', 'total_rejected_items']),
    rejection_proceed_en: entry('rejection_proceed_en', REJECTION_PAGE, ['mobile_number']),
    rejection_fsn_opened_en: entry('rejection_fsn_opened_en', REJECTION_PAGE, ['mobile_number']),
    rejection_fsn_submitted_en: entry('rejection_fsn_submitted_en', REJECTION_PAGE, ['mobile_number', 'relation', 'relation_changed']),
    rejection_fsn_error_en: entry('rejection_fsn_error_en', REJECTION_PAGE, ['mobile_number', 'relation', 'relation_changed', 'code']),
    digilocker_redo_initiated_en: entry('digilocker_redo_initiated_en', REJECTION_PAGE, ['mobile_number', 'session_type']),
    selfie_redo_initiated_en: entry('selfie_redo_initiated_en', REJECTION_PAGE, ['mobile_number', 'session_type']),
});

/**
 * @param {string} legacy  legacy per-page name (EVENTS.*)
 * @param {Record<string, unknown>} [props]
 * @returns {{ name: string, props: Record<string, unknown> } | null}  null when not a journey event
 */
export function resolveJourneyEvent(legacy, props = {}) {
    const e = EKYC_JOURNEY_MAP[legacy];
    if (!e) return null;
    return { name: e.event, props: { ...props, ...e.fixed } };
}

/** Journey, main or other-event entry for a legacy name, or null. */
export const getParityEntry = (legacy) => EKYC_JOURNEY_MAP[legacy] || EKYC_MAIN_EVENTS[legacy]
    || EKYC_OTHER_EVENTS[legacy] || null;

/**
 * Name + props MoEngage should receive for `legacy`. Events in neither map
 * pass through unchanged.
 * @param {string} legacy
 * @param {Record<string, unknown>} [props]
 * @returns {{ name: string, props: Record<string, unknown> }}
 */
export const resolveMoEngageEvent = (legacy, props = {}) => {
    const e = getParityEntry(legacy);
    if (!e) return { name: legacy, props };
    return { name: e.event, props: { ...props, ...e.fixed } };
};
